import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { WarehouseCheckInService } from "src/services";
import { QueryParamsMapper } from "src/mappers";
import { queryKeys } from "src/constants";
import { WarehouseCheckIn } from "src/types/database";
import {
  DateTimeRange,
  WarehouseCheckInsPaginationOptions,
  PaginatedWarehouseCheckIns,
  WarehouseCheckInsFilters,
  UpdateWarehouseCheckInOpsNoteParams
} from "src/types";

export const useUpdateWarehouseCheckInOpsNote = (
  warehouseCheckInService: WarehouseCheckInService,
  dateTimeRange?: DateTimeRange,
  filter?: WarehouseCheckInsFilters,
  paginationOptions?: WarehouseCheckInsPaginationOptions
) => {
  const queryClient = useQueryClient();
  const queryParams = QueryParamsMapper.getWarehouseCheckInsQueryParams(dateTimeRange, filter, paginationOptions);
  const [lastVariables, setLastVariables] = useState<UpdateWarehouseCheckInOpsNoteParams | null>(null);

  const updateWarehouseCheckInInCache = (updatedWarehouseCheckIn: WarehouseCheckIn) => {
    if (dateTimeRange || filter || paginationOptions) {
      queryClient.setQueryData(
        queryKeys.paginatedWarehouseCheckIns(queryParams),
        (oldPaginatedData?: PaginatedWarehouseCheckIns) => ({
          ...oldPaginatedData,
          items: oldPaginatedData?.items?.map((warehouseCheckIn) =>
            warehouseCheckIn.id === updatedWarehouseCheckIn.id ? updatedWarehouseCheckIn : warehouseCheckIn
          )
        })
      );
    }

    queryClient.setQueryData(
      queryKeys.warehouseCheckIn(updatedWarehouseCheckIn.id, updatedWarehouseCheckIn.timestamp),
      () => updatedWarehouseCheckIn
    );
  };

  const {
    mutate: updateOpsNote,
    isPending: isUpdating,
    error: updateError,
    reset: resetUpdate
  } = useMutation<WarehouseCheckIn, Error, UpdateWarehouseCheckInOpsNoteParams>({
    mutationFn: ({ id, timestamp, opsNote }) =>
      warehouseCheckInService.updateWarehouseCheckInOpsNote(id, timestamp, opsNote),
    onSuccess: updateWarehouseCheckInInCache,
    onMutate: (variables) => {
      setLastVariables(variables);
    }
  });

  const retryUpdate = () => {
    if (lastVariables) {
      updateOpsNote(lastVariables);
    }
  };

  return { updateOpsNote, isUpdating, updateError, retryUpdate, resetUpdate };
};
